import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Caption1,
  Checkbox,
  MessageBar,
  MessageBarBody,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  tokens,
} from '@fluentui/react-components';
import { CheckmarkCircleFilled } from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import type { FunctionDescriptorDto, ManagedRoleDto } from '../api/types';

/**
 * Pflege der Rollen-Berechtigungen (Variante 2) — Tab innerhalb der Einstellungen.
 * Matrix Funktion × App-Rolle: für Nicht-Admin-Rollen (z. B. Kunde) wird pro Funktion
 * die Allow-Liste gepflegt. Die Admin-Rolle hat implizit Vollzugriff (read-only).
 * Der Admin-Schutz wird von der umgebenden Settings-Seite gestellt.
 */
export function RoleAccessSettings() {
  const api = useApi();
  const state = useAsync(() => api.getRoleCatalog(), [api]);
  const [allowed, setAllowed] = useState<Record<string, Set<string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [savingRole, setSavingRole] = useState<string | null>(null);

  useEffect(() => {
    if (state.data) {
      const map: Record<string, Set<string>> = {};
      for (const r of state.data.roles) {
        if (!r.isAdmin) map[r.value] = new Set(r.allowedFunctions);
      }
      setAllowed(map);
    }
  }, [state.data]);

  const nonAdminRoles = useMemo(
    () => (state.data?.roles ?? []).filter((r) => !r.isAdmin),
    [state.data],
  );
  const adminRoles = useMemo(
    () => (state.data?.roles ?? []).filter((r) => r.isAdmin),
    [state.data],
  );

  // Funktionen nach Kategorie gruppieren (Reihenfolge des Katalogs beibehalten).
  const grouped = useMemo(() => {
    const out: { category: string; items: FunctionDescriptorDto[] }[] = [];
    for (const f of state.data?.functions ?? []) {
      let g = out.find((x) => x.category === f.category);
      if (!g) {
        g = { category: f.category, items: [] };
        out.push(g);
      }
      g.items.push(f);
    }
    return out;
  }, [state.data]);

  async function toggle(role: ManagedRoleDto, key: string, checked: boolean) {
    const next = new Set(allowed[role.value] ?? []);
    if (checked) next.add(key);
    else next.delete(key);
    setAllowed((prev) => ({ ...prev, [role.value]: next }));
    setSavingRole(role.value);
    try {
      await api.setRolePermissions(role.value, [...next]);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      // Bei Fehler den Katalog neu laden, um den echten Stand zu zeigen.
      state.reload?.();
    } finally {
      setSavingRole(null);
    }
  }

  if (state.loading) return <Spinner label="Lade Rollen…" />;
  if (state.error) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{state.error.message}</MessageBarBody>
      </MessageBar>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
      <Caption1>
        Funktionen, die eine Rolle nutzen darf. Nicht angehakte Funktionen sind für die Rolle
        ausgeblendet und serverseitig gesperrt (deny-by-default). Änderungen wirken nach der
        nächsten Anmeldung des Nutzers. Zuweisung der Rolle: Entra → Enterprise Applications →
        M365 Adminbox API → Benutzer und Gruppen.
      </Caption1>

      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}

      {nonAdminRoles.map((r) => (
        <Caption1 key={r.value}>
          <Text weight="semibold">{r.displayName}</Text> — {r.description}
        </Caption1>
      ))}

      <Table size="small" aria-label="Rollen-Berechtigungen">
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Funktion</TableHeaderCell>
            {nonAdminRoles.map((r) => (
              <TableHeaderCell key={r.value} style={{ textAlign: 'center' }}>
                {r.displayName}
                {savingRole === r.value ? <Spinner size="tiny" style={{ marginInlineStart: 6 }} /> : null}
              </TableHeaderCell>
            ))}
            {adminRoles.map((r) => (
              <TableHeaderCell key={r.value} style={{ textAlign: 'center' }}>
                {r.displayName}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {grouped.map((g) => (
            <Fragment key={g.category}>
              <TableRow key={`cat-${g.category}`}>
                <TableCell colSpan={1 + nonAdminRoles.length + adminRoles.length}>
                  <Text weight="semibold" size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                    {g.category}
                  </Text>
                </TableCell>
              </TableRow>
              {g.items.map((f) => (
                <TableRow key={f.key}>
                  <TableCell>
                    {f.label}
                    {f.adminOnly ? (
                      <Badge appearance="tint" color="informative" size="small" style={{ marginInlineStart: 6 }}>
                        admin
                      </Badge>
                    ) : null}
                  </TableCell>
                  {nonAdminRoles.map((r) => (
                    <TableCell key={r.value} style={{ textAlign: 'center' }}>
                      {f.adminOnly ? (
                        <Text size={200} style={{ color: tokens.colorNeutralForeground4 }}>
                          —
                        </Text>
                      ) : (
                        <Checkbox
                          checked={(allowed[r.value] ?? new Set()).has(f.key)}
                          onChange={(_, d) => toggle(r, f.key, Boolean(d.checked))}
                        />
                      )}
                    </TableCell>
                  ))}
                  {adminRoles.map((r) => (
                    <TableCell key={r.value} style={{ textAlign: 'center' }}>
                      <CheckmarkCircleFilled style={{ color: tokens.colorPaletteGreenForeground1 }} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
